/*
 *   File : report.js
 *   Author URI : https://evoqins.com
 *   Description : Report page
 *   Integrations : NA
 *   Version : v1.1
 */

import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { format } from "date-fns";

import { GradientButton, OutlineButton } from "../../../../Components/Cta";
import { DateRangeFilterPopup } from "../../../../Components/Popups";
import { DatePicker, CustomSearchSelectBox } from "../../../../Components/FormElements";
import { CustomTab } from "../../../../Components/Tab";
import { Icon } from "../../../../Components/Icon";

import APIService from "../../../../Services/api-service";
import { CustomLoader } from "../../../../Components/Other";



const TAB_DATA = ['Transactions', 'Portfolio summary', 'Capital gain', 'Overall gain/loss', 'Consolidated portfolio', 'Unrealized gain loss summary', 'Unrealized gain loss details']; // 'Client portfolio summary',

const ALL_OPTION = { value: "", label: "All" };

const TRANSACTION_REPORT_DOWNLOAD_V2_URL = 'reports/transaction-report-v2';
const TRANSACTION_REPORT_PREVIEW_V2_URL = 'reports/transaction-report-list-v2';

export default function Report(props) {

    const [transactionStartDate, setTransactionStartDate] = useState(null);
    const [transactionsEndDate, setTransactionsEndDate] = useState(null);
    const [showTransactionPopUp, setShowTransactionPopUp] = useState(false);
    const [transactionRange, setTransactionRange] = useState({
        startDate: new Date(),
        endDate: new Date(),
        key: 'selection',
    });
    const [capitalStartDate, setCapitalStartDate] = useState(null);
    const [capitalEndDate, setCapitalEndDate] = useState(null);
    const [capitalRange, setCapitalRange] = useState({
        startDate: new Date(),
        endDate: new Date(),
        key: 'selection',
    });
    const [showCapitalPopUp, setShowCapitalPopUp] = useState(false);
    const [portfolioDate, setPortfolioDate] = useState(null);
    const [transactionLoader, setTransactionLoader] = useState(false);
    const [capitalLoader, setCapitalLoader] = useState(false);
    const [portfolioLoader, setPortfolioLoader] = useState(false);
    const [tabIndex, setTabIndex] = useState(1);
    const [reportHtml, setReportHtml] = useState(null);
    const [reportLoader, setReportLoader] = useState(false);
    const [financialYears, setFinancialYears] = useState([]);
    const [year, setYear] = useState('');
    const [summaryDate, setSummaryDate] = useState(null);
    const [gainLossDate, setGainLossDate] = useState(null);
    const [consolidatedPortfolioDate, setConsolidatedPortfolioDate] = useState(null);
    const [unrealizedGainLossSummaryDate, setUnrealizedGainLossSummaryDate] = useState(null);
    const [unrealizedGainLossDetailDate, setUnrealizedGainLossDetailDate] = useState(null);
    const [transactionType, setTransactionType] = useState(ALL_OPTION);
    const [subTransactionType, setSubTransactionType] = useState(ALL_OPTION);
    const [transactionTypeOptions, setTransactionTypeOptions] = useState([ALL_OPTION]);
    const [subTransactionTypeOptions, setSubTransactionTypeOptions] = useState([ALL_OPTION]);

    // scroll to top
    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, []);

    useEffect(() => {
        if (tabIndex === 3) {
            _filterData();
        }
    }, [tabIndex]);

    useEffect(() => {
        if (tabIndex === 1) {
            _getTransactionFilterData();
        }
    }, [tabIndex]);

    // handle transaction date picker
    function _handleTransactionRangeChange(newRange) {
        setShowTransactionPopUp(false);
        setTransactionStartDate(newRange.startDate);
        setTransactionsEndDate(newRange.endDate);
        setTransactionRange(newRange);
    };

    // clear transaction date picker
    function _handleTransactionDate(clear) {
        setShowTransactionPopUp(!showTransactionPopUp);
        if (clear) {
            setTransactionStartDate(null);
            setTransactionsEndDate(null);
            setTransactionRange({
                startDate: new Date(),
                endDate: new Date(),
                key: 'selection',
            })
        }
    }

    // handle capital date picker
    function _handleCapitalRangeChange(newRange) {
        setShowCapitalPopUp(false);
        setCapitalStartDate(newRange.startDate);
        setCapitalEndDate(newRange.endDate);
        setCapitalRange(newRange);
    };

    // clear capital date picker
    function _handleCapitalDate(clear) {
        setShowCapitalPopUp(!showCapitalPopUp);
        if (clear) {
            setCapitalStartDate(null);
            setCapitalEndDate(null);
            setCapitalRange({
                startDate: new Date(),
                endDate: new Date(),
                key: 'selection',
            })
        }
    };

    // handle tab change
    function _handleTabSelect(index) {
        setTabIndex(index);
        setReportHtml(null);
        setReportLoader(false);
    }

    function _filterData() {
        let url = 'reports/filter-data';
        APIService(false, url).then((response) => {
            if (response.status_code === 200) {
                let years = response.data.map((item, key) => {
                    return { value: item.id, label: item.name }
                })
                setFinancialYears(years);
            }
        })
    }

    // maps a raw filter-data entry (string or {id/name} object) to a select option
    function _mapFilterOptions(list) {
        if (!Array.isArray(list)) {
            return [];
        }

        return list.map((item) => {
            if (typeof item === "string") {
                return { value: item, label: item };
            }

            return {
                value: item.id ?? item.value,
                label: item.label ?? item.name
            };
        });
    }

    // builds select options from a filter-data list, keeping a single "All" option in front
    // (some endpoints already return their own "ALL" entry; avoid showing two)
    function _buildFilterOptions(list) {
        const mapped_options = _mapFilterOptions(list);
        const all_index = mapped_options.findIndex((item) => typeof item.label === "string" && item.label.trim().toLowerCase() === "all");

        if (all_index === -1) {
            return [ALL_OPTION, ...mapped_options];
        }

        const [all_option] = mapped_options.splice(all_index, 1);
        return [all_option, ...mapped_options];
    }

    // API :: transaction type / sub transaction type filter options
    function _getTransactionFilterData() {
        let url = 'reports/transaction-filter-data';
        APIService(false, url).then((response) => {
            if (response.status_code === 200) {
                const type_list = response.data.transaction_type || response.data.transaction_types || [];
                const sub_type_list = response.data.sub_transaction_type || response.data.sub_transaction_types || [];

                const transaction_type_options = _buildFilterOptions(type_list);
                const sub_transaction_type_options = _buildFilterOptions(sub_type_list);

                setTransactionTypeOptions(transaction_type_options);
                setSubTransactionTypeOptions(sub_transaction_type_options);
                setTransactionType(transaction_type_options[0]);
                setSubTransactionType(sub_transaction_type_options[0]);
            }
        })
    }
    // file downloader
    function _fileDownloader(url, file_name) {
        const link = document.createElement('a');
        link.href = url;
        link.download = file_name;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    function _validateTransactionDateRange() {
        if (transactionStartDate !== null && transactionsEndDate !== null && transactionStartDate > transactionsEndDate) {
            toast.dismiss();
            toast.error('From date can not be greater than to date', {
                type: 'error'
            });
            return false;
        }

        return true;
    }

    function _getTransactionReportPayload(fileType) {
        let start_date = null;
        let end_date = null;

        if (transactionStartDate !== null && transactionsEndDate !== null) {
            start_date = format(transactionStartDate, "dd-MM-yyyy");
            end_date = format(transactionsEndDate, "dd-MM-yyyy");
        }

        const payload = {
            from_date: start_date,
            till_date: end_date,
            customer_id: props.userId,
            transaction_type: transactionType ? transactionType.value : "",
            sub_transaction_type: subTransactionType ? subTransactionType.value : ""
        };

        if (fileType) {
            payload.file_type = fileType;
        }

        return payload;
    }

    // API :: transaction report
    function _downloadTransactionsReport() {
        if (!_validateTransactionDateRange()) {
            return;
        }

        setTransactionLoader(true);
        let url = TRANSACTION_REPORT_DOWNLOAD_V2_URL;
        let payload = JSON.stringify(_getTransactionReportPayload(1));
        APIService(true, url, payload).then((response) => {
            if (response.status_code === 200) {
                if (response.data.url !== null) {
                    _fileDownloader(response.data.url, 'transactions.pdf')
                } else {
                    toast.dismiss();
                    toast.error('No reports for download!', {
                        type: 'error'
                    })
                }
            } else {
                toast.dismiss();
                toast.error(response.message, {
                    type: 'error'
                })
            }
            setTransactionLoader(false);
        });
    };

    // API :: capital report 
    function _downloadCapitalReport() {
        setCapitalLoader(true);
        let start_date = null;
        let end_date = null;
        // if (capitalStartDate !== null && capitalEndDate !== null) {
        //     start_date = format(capitalStartDate, "dd-MM-yyyy");
        //     end_date = format(capitalEndDate, "dd-MM-yyyy");
        // }
        let url = 'reports/capitalgain-report';
        let payload = JSON.stringify({
            // from_date: start_date,
            // till_date: end_date,
            file_type: 1,
            id: year.value ? year.value : null,
            customer_id: props.userId
        });
        APIService(true, url, payload).then((response) => {
            if (response.status_code === 200) {
                if (response.data.url !== null) {
                    _fileDownloader(response.data.url, 'transactions.pdf')
                } else {
                    toast.dismiss();
                    toast.error('No reports for download!', {
                        type: 'error'
                    })
                }
            } else {
                toast.dismiss();
                toast.error(response.message, {
                    type: 'error'
                })
            }
            setCapitalLoader(false);
        });
    }

    // API :: portfolio summary
    function _downloadPortfolio() {
        setPortfolioLoader(true);
        let url = 'reports/portfolio';
        let payload = JSON.stringify({
            till_date: portfolioDate,
            file_type: 1,
            customer_id: props.userId
        });
        APIService(true, url, payload).then((response) => {
            if (response.status_code === 200) {
                if (response.data.url !== null) {
                    _fileDownloader(response.data.url, 'transactions.pdf')
                } else {
                    toast.dismiss();
                    toast.error('No reports for download!', {
                        type: 'error'
                    })
                }
            } else {
                toast.dismiss();
                toast.error(response.message, {
                    type: 'error'
                });
            }
            setPortfolioLoader(false);
        });
    }

    function _viewTransactionReport() {
        if (!_validateTransactionDateRange()) {
            return;
        }

        setReportLoader(true);
        let url = TRANSACTION_REPORT_PREVIEW_V2_URL;
        let payload = JSON.stringify(_getTransactionReportPayload());
        APIService(true, url, payload).then((response) => {
            if (response.status_code === 200) {
                setReportHtml(response.data);
                toast.dismiss();
                toast.success('Transaction report generated', {
                    type: 'success'
                })
            } else {
                setReportHtml(null);
                toast.dismiss();
                toast.error(response.message, {
                    type: 'error'
                })
            }
            setReportLoader(false);
        })

    }

    function _viewPortfolioReport() {
        setReportLoader(true);
        let url = 'reports/portfolio-list';
        let payload = JSON.stringify({
            till_date: portfolioDate,
            file_type: 1,
            customer_id: props.userId
        });
        APIService(true, url, payload).then((response) => {
            if (response.status_code === 200) {
                setReportHtml(response.data);
                toast.dismiss();
                toast.success('Portfolio report generated', {
                    type: 'success'
                })
            } else {
                setReportHtml(null);
                toast.dismiss();
                toast.error(response.message, {
                    type: 'error'
                })
            }
            setReportLoader(false);
        })

    }

    function _viewCapitalReport() {
        setReportLoader(true);
        let url = 'reports/capitalgain-report-list';
        let start_date = null;
        let end_date = null;
        // if (capitalStartDate !== null && capitalEndDate !== null) {
        //     start_date = format(capitalStartDate, "dd-MM-yyyy");
        //     end_date = format(capitalEndDate, "dd-MM-yyyy");
        // }
        let payload = JSON.stringify({
            // from_date: start_date,
            // till_date: end_date,
            id: year.value ? year.value : null,
            customer_id: props.userId
        });
        APIService(true, url, payload).then((response) => {
            if (response.status_code === 200) {
                setReportHtml(response.data);
                toast.dismiss();
                toast.success('Capital gain report generated', {
                    type: 'success'
                })
            } else {
                setReportHtml(null);
                toast.dismiss();
                toast.error(response.message, {
                    type: 'error'
                })
            }
            setReportLoader(false);
        })

    }

    // API - view client portfolio summary
    function _viewPortfolioSummaryReport() {
        setReportLoader(true);
        let url = 'reports/portfolio-summary';
        let payload = JSON.stringify({
            till_date: summaryDate,
            customer_id: props.userId
        });
        APIService(true, url, payload).then((response) => {
            if (response.status_code === 200) {
                setReportHtml(response.data);
                toast.success('Client portfolio summary generated', {
                    type: 'success'
                })
            } else {
                setReportHtml(null);
                toast.dismiss();
                toast.error(response.message, {
                    type: 'error'
                })
            }
            setReportLoader(false);
        })
    }

    function _downloadPortfolioSummary() {
        setPortfolioLoader(true);
        let url = 'reports/portfolio-summary-download';
        let payload = JSON.stringify({
            till_date: summaryDate,
            customer_id: props.userId
        });
        APIService(true, url, payload).then((response) => {
            if (response.status_code === 200) {
                if (response.data.url !== null) {
                    _fileDownloader(response.data.url, 'client-portfolio-summary.pdf')
                } else {
                    toast.dismiss();
                    toast.error('No reports for download!', {
                        type: 'error'
                    })
                }
            } else {
                toast.dismiss();
                toast.error(response.message, {
                    type: 'error'
                });
            }
            setPortfolioLoader(false);
        });
    }

    // API - view overall gain/loss summary
    function _viewGainLoss() {
        setReportLoader(true);
        let url = 'reports/gain-loss-list';
        let payload = JSON.stringify({
            till_date: gainLossDate,
            customer_id: props.userId
        });
        APIService(true, url, payload).then((response) => {
            if (response.status_code === 200) {
                setReportHtml(response.data);
                toast.success('Client portfolio summary generated', {
                    type: 'success'
                })
            } else {
                setReportHtml(null);
                toast.dismiss();
                toast.error(response.message, {
                    type: 'error'
                })
            }
            setReportLoader(false);
        })
    }

    // download gain/loss
    function _downloadGainLoss() {
        setPortfolioLoader(true);
        let url = 'reports/gain-loss';
        let payload = JSON.stringify({
            till_date: gainLossDate,
            customer_id: props.userId
        });
        APIService(true, url, payload).then((response) => {
            if (response.status_code === 200) {
                if (response.data.url !== null) {
                    _fileDownloader(response.data.url, 'client-portfolio-summary.pdf')
                } else {
                    toast.dismiss();
                    toast.error('No reports for download!', {
                        type: 'error'
                    })
                }
            } else {
                toast.dismiss();
                toast.error(response.message, {
                    type: 'error'
                });
            }
            setPortfolioLoader(false);
        });
    }

    // API - view overall gain/loss summary
    function _viewConsolidatedPortfolio() {
        setReportLoader(true);
        let url = 'reports/consolidated-portfolio-list';
        let payload = JSON.stringify({
            till_date: consolidatedPortfolioDate,
            type: 1,
            customer_id: props.userId
        });
        APIService(true, url, payload).then((response) => {
            if (response.status_code === 200) {
                setReportHtml(response.data);
                toast.success('Consolidated portfolio summary generated', {
                    type: 'success'
                })
            } else {
                setReportHtml(null);
                toast.dismiss();
                toast.error(response.message, {
                    type: 'error'
                })
            }
            setReportLoader(false);
        })
    }

    // download gain/loss
    function _downloadConsolidatedPortfolio() {
        setPortfolioLoader(true);
        let url = 'reports/consolidated-portfolio';
        let payload = JSON.stringify({
            till_date: consolidatedPortfolioDate,
            type: 1,
            customer_id: props.userId
        });
        APIService(true, url, payload).then((response) => {
            if (response.status_code === 200) {
                if (response.data.url !== null) {
                    _fileDownloader(response.data.url, 'consolidated-portfolio.pdf')
                } else {
                    toast.dismiss();
                    toast.error('No reports for download!', {
                        type: 'error'
                    })
                }
            } else {
                toast.dismiss();
                toast.error(response.message, {
                    type: 'error'
                });
            }
            setPortfolioLoader(false);
        });
    }

    // view unrealized gain loss summary
    function _viewUnrealizedSummary() {
        setReportLoader(true);
        let url = 'reports/unrealized-gain-loss-list';
        let payload = JSON.stringify({
            till_date: unrealizedGainLossSummaryDate,
            customer_id: props.userId
        });
        APIService(true, url, payload).then((response) => {
            if (response.status_code === 200) {
                setReportHtml(response.data);
                toast.success('Unrealized gain loss summary', {
                    type: 'success'
                })
            } else {
                setReportHtml(null);
                toast.dismiss();
                toast.error(response.message, {
                    type: 'error'
                })
            }
            setReportLoader(false);
        });
    }

    // Download unrealized summary report
    function _downloadUnrealizedSummary() {
        setPortfolioLoader(true);
        let url = 'reports/unrealized-gain-loss';
        let payload = JSON.stringify({
            till_date: unrealizedGainLossSummaryDate,
            customer_id: props.userId
        });
        APIService(true, url, payload).then((response) => {
            if (response.status_code === 200) {
                if (response.data.url !== null) {
                    _fileDownloader(response.data.url, 'unrealized-gain-loss-summary.pdf')
                } else {
                    toast.dismiss();
                    toast.error('No reports for download!', {
                        type: 'error'
                    })
                }
            } else {
                toast.dismiss();
                toast.error(response.message, {
                    type: 'error'
                });
            }
            setPortfolioLoader(false);
        });
    }


    // view unrealized gain loss detail
    function _viewUnrealizedDetail() {
        setReportLoader(true);
        let url = 'reports/unrealized-gain-loss-details-list';
        let payload = JSON.stringify({
            till_date: unrealizedGainLossDetailDate,
            customer_id: props.userId
        });
        APIService(true, url, payload).then((response) => {
            if (response.status_code === 200) {
                setReportHtml(response.data);
                toast.success('Unrealized gain loss detail generated', {
                    type: 'success'
                })
            } else {
                setReportHtml(null);
                toast.dismiss();
                toast.error(response.message, {
                    type: 'error'
                })
            }
            setReportLoader(false);
        });
    }

    // Download unrealized summary report
    function _downloadUnrealizedDetail() {
        setPortfolioLoader(true);
        let url = 'reports/unrealized-gain-loss-details';
        let payload = JSON.stringify({
            till_date: unrealizedGainLossDetailDate,
            customer_id: props.userId
        });
        APIService(true, url, payload).then((response) => {
            if (response.status_code === 200) {
                if (response.data.url !== null) {
                    _fileDownloader(response.data.url, 'unrealized-gain-loss-detail.pdf')
                } else {
                    toast.dismiss();
                    toast.error('No reports for download!', {
                        type: 'error'
                    })
                }
            } else {
                toast.dismiss();
                toast.error(response.message, {
                    type: 'error'
                });
            }
            setPortfolioLoader(false);
        });
    }

    return (
        <>
            <div className="e-page-container margin-80px-bottom min-height-100vh ">

                <div className="row">
                    <div className="col-12">
                        <div className="e-tap-wrapper">
                            <CustomTab data={TAB_DATA}
                                wrapperClass={'gap-16px'}
                                index={tabIndex}
                                className="px-0 padding-10px-tb e-order-tab mb-4"
                                onSelectTab={(value) => _handleTabSelect(value)} />
                        </div>
                    </div>
                    {
                        tabIndex === 1 ? (
                            <div className="row gx-2">
                                <div className="col-lg-3 col-md-4">
                                    <div className="position-relative  e-margin-left-auto">
                                        <p className={`border-radius-6px border-all bg-white px-2 padding-12px-tb mb-0 color-outer-space e-poppins-regular e-font-14 line-height-24px cursor-pointer position-relative ${showTransactionPopUp === true ? 'border-primary-color' : 'border-bright-gray'}`}
                                            onClick={() => {
                                                _handleTransactionDate(false);
                                            }}>
                                            {transactionStartDate === null ? 'Start date' : format(transactionStartDate, "dd MMM yy")} - {transactionsEndDate === null ? 'End date' : format(transactionsEndDate, "dd MMM yy")}
                                            <Icon icon="calendarIcon"
                                                className='position-absolute right-10px top-14px'
                                                size={20} />
                                        </p>
                                        {
                                            showTransactionPopUp === true &&
                                            <DateRangeFilterPopup
                                                maxDate={new Date()}
                                                initialRange={transactionRange}
                                                leftOffset='left-0'
                                                cancel={() => {
                                                    _handleTransactionDate(true)
                                                }}
                                                leftAlign={true}
                                                onUpdate={_handleTransactionRangeChange} />
                                        }
                                    </div>
                                </div>
                                <div className="col-lg-2 col-md-2">
                                    <CustomSearchSelectBox value={transactionType}
                                        label="Transaction type"
                                        className="margin-32px-bottom "
                                        options={transactionTypeOptions}
                                        onSelectChange={(value) => setTransactionType(value)} />
                                </div>
                                <div className="col-lg-2 col-md-2">
                                    <CustomSearchSelectBox value={subTransactionType}
                                        label="Sub transaction type"
                                        className="margin-32px-bottom "
                                        options={subTransactionTypeOptions}
                                        onSelectChange={(value) => setSubTransactionType(value)} />
                                </div>
                                <div className="col-lg-5 col-md-4 col-sm-12 col-12 text-end mt-lg-0 mt-md-0 mt-sm-4 mt-4">
                                    <OutlineButton label="View Report"
                                        className="padding-14px-tb px-3 e-font-16 me-3"
                                        loading={reportLoader}
                                        onPress={_viewTransactionReport} />
                                    <GradientButton label="Download Report"
                                        className="padding-14px-tb padding-18px-lr e-font-16"
                                        loading={transactionLoader}
                                        onPress={_downloadTransactionsReport} />
                                </div>

                            </div>
                        ) : tabIndex === 2 ? (
                            <div className="row   gx-2">
                                <div className="col-lg-4 col-md-6 col-sm-6 col-12 position-relative">
                                    <DatePicker label="Choose date"
                                        value={portfolioDate}
                                        maxDate={new Date()}
                                        dateFormat={'dd-MM-yyyy'}
                                        displayFormat={'dd MM yyyy'}
                                        removeLabelOnFocus={true}
                                        dob={(value) => setPortfolioDate(value)} />
                                </div>
                                <div className="col-lg-8 col-md-6 col-sm-6 col-12 text-end mt-lg-0 mt-md-0 mt-sm-0 mt-4">
                                    <OutlineButton label="View Report"
                                        className="padding-12px-tb px-3 e-font-16 me-3"
                                        loading={reportLoader}
                                        onPress={_viewPortfolioReport} />
                                    <GradientButton label="Download Report"
                                        className="padding-12px-tb padding-18px-lr e-font-16"
                                        loading={portfolioLoader}
                                        onPress={_downloadPortfolio} />
                                </div>
                            </div>
                        ) : tabIndex === 3 ? (
                            <div className="row gx-2">
                                {/* <div className="col-md-4 ">
                                    <div className="position-relative e-margin-left-auto">

                                        <p className={`border-radius-6px border-all bg-white px-2 padding-12px-tb mb-0 color-outer-space e-poppins-regular e-font-14 line-height-24px cursor-pointer position-relative ${showCapitalPopUp === true ? 'border-primary-color' : 'border-bright-gray'}`}
                                            onClick={() => {
                                                _handleCapitalDate(false);
                                            }}>
                                            {capitalStartDate === null ? 'Start date' : format(capitalStartDate, "dd MMM yy")} - {capitalEndDate === null ? 'End date' : format(capitalEndDate, "dd MMM yy")}
                                            <Icon icon="calendarIcon"
                                                className='position-absolute right-10px top-14px'
                                                size={20} />
                                        </p>
                                        {
                                            showCapitalPopUp === true &&
                                            <DateRangeFilterPopup
                                                maxDate={new Date()}
                                                initialRange={capitalRange}
                                                cancel={() => {
                                                    _handleCapitalDate(true)
                                                }}
                                                leftAlign={true}
                                                onUpdate={_handleCapitalRangeChange} />
                                        }
                                    </div>
                                </div> */}
                                <div className="col-md-2">
                                    <CustomSearchSelectBox value={year}
                                        label="Financial year"
                                        // error={}
                                        className="margin-32px-bottom "
                                        options={financialYears}
                                        onSelectChange={(value) => setYear(value)} />
                                </div>
                                <div className="col-lg-10 col-md-6 col-sm-6 col-12 text-end mt-lg-0 mt-md-0 mt-sm-0 mt-4">
                                    <OutlineButton label="View Report"
                                        loading={reportLoader}
                                        className="padding-12px-tb px-3 e-font-16 me-3"
                                        onPress={_viewCapitalReport} />
                                    <GradientButton label="Download Report"
                                        className="padding-12px-tb padding-18px-lr e-font-16"
                                        loading={capitalLoader}
                                        onPress={_downloadCapitalReport} />
                                </div>

                            </div>
                        ) : tabIndex === 4 ? (
                            <div className="row   gx-2">
                                <div className="col-lg-4 col-md-6 col-sm-6 col-12 position-relative">
                                    <DatePicker label="Till date"
                                        value={summaryDate}
                                        maxDate={new Date()}
                                        dateFormat={'dd-MM-yyyy'}
                                        displayFormat={'dd MM yyyy'}
                                        removeLabelOnFocus={true}
                                        dob={(value) => setGainLossDate(value)} />
                                </div>
                                <div className="col-lg-8 col-md-6 col-sm-6 col-12 text-end mt-lg-0 mt-md-0 mt-sm-0 mt-4">
                                    <OutlineButton label="View Report"
                                        className="padding-12px-tb px-3 e-font-16 me-3"
                                        loading={reportLoader}
                                        onPress={_viewGainLoss} />
                                    <GradientButton label="Download Report"
                                        className="padding-12px-tb padding-18px-lr e-font-16"
                                        loading={portfolioLoader}
                                        onPress={_downloadGainLoss} />
                                </div>
                            </div>
                        ) : tabIndex === 5 ? (
                            <div className="row   gx-2">
                                <div className="col-lg-4 col-md-6 col-sm-6 col-12 position-relative">
                                    <DatePicker label="Till date"
                                        value={consolidatedPortfolioDate}
                                        maxDate={new Date()}
                                        dateFormat={'dd-MM-yyyy'}
                                        displayFormat={'dd MM yyyy'}
                                        removeLabelOnFocus={true}
                                        dob={(value) => setConsolidatedPortfolioDate(value)} />
                                </div>
                                <div className="col-lg-8 col-md-6 col-sm-6 col-12 text-end mt-lg-0 mt-md-0 mt-sm-0 mt-4">
                                    <OutlineButton label="View Report"
                                        className="padding-12px-tb px-3 e-font-16 me-3"
                                        loading={reportLoader}
                                        onPress={_viewConsolidatedPortfolio} />
                                    <GradientButton label="Download Report"
                                        className="padding-12px-tb padding-18px-lr e-font-16"
                                        loading={portfolioLoader}
                                        onPress={_downloadConsolidatedPortfolio} />
                                </div>
                            </div>
                        ) : tabIndex === 6 ? (
                            <div className="row   gx-2">
                                <div className="col-lg-4 col-md-6 col-sm-6 col-12 position-relative">
                                    <DatePicker label="Till date"
                                        value={unrealizedGainLossSummaryDate}
                                        maxDate={new Date()}
                                        dateFormat={'dd-MM-yyyy'}
                                        displayFormat={'dd MM yyyy'}
                                        removeLabelOnFocus={true}
                                        dob={(value) => setUnrealizedGainLossSummaryDate(value)} />
                                </div>
                                <div className="col-lg-8 col-md-6 col-sm-6 col-12 text-end mt-lg-0 mt-md-0 mt-sm-0 mt-4">
                                    <OutlineButton label="View Report"
                                        className="padding-12px-tb px-3 e-font-16 me-3"
                                        loading={reportLoader}
                                        onPress={_viewUnrealizedSummary} />
                                    <GradientButton label="Download Report"
                                        className="padding-12px-tb padding-18px-lr e-font-16"
                                        loading={portfolioLoader}
                                        onPress={_downloadUnrealizedSummary} />
                                </div>
                            </div>
                        ) : (<div className="row   gx-2">
                            <div className="col-lg-4 col-md-6 col-sm-6 col-12 position-relative">
                                <DatePicker label="Till date"
                                    value={unrealizedGainLossDetailDate}
                                    maxDate={new Date()}
                                    dateFormat={'dd-MM-yyyy'}
                                    displayFormat={'dd MM yyyy'}
                                    removeLabelOnFocus={true}
                                    dob={(value) => setUnrealizedGainLossDetailDate(value)} />
                            </div>
                            <div className="col-lg-8 col-md-6 col-sm-6 col-12 text-end mt-lg-0 mt-md-0 mt-sm-0 mt-4">
                                <OutlineButton label="View Report"
                                    className="padding-12px-tb px-3 e-font-16 me-3"
                                    loading={reportLoader}
                                    onPress={_viewUnrealizedDetail} />
                                <GradientButton label="Download Report"
                                    className="padding-12px-tb padding-18px-lr e-font-16"
                                    loading={portfolioLoader}
                                    onPress={_downloadUnrealizedDetail} />
                            </div>
                        </div>
                        )
                    }

                    {
                        reportLoader === true &&
                        <div className="row">
                            <div className="col-12 h-80vh">
                                <CustomLoader />
                            </div>
                        </div>
                    }

                    {
                        reportHtml !== null &&
                        <div className="row mt-3">
                            <div className="col-12 mt-4">
                                <div className="e-report-view-wrapper " dangerouslySetInnerHTML={{ __html: reportHtml }}></div>
                            </div>
                        </div>
                    }


                </div >
            </div >
        </>
    )
}
